/**
 * Rauwe PCM van de server omzetten naar iets dat de browser kan afspelen.
 *
 * BEVAT DE REPARATIE VAN EEN BUG UIT HET OUDE CONCEPT. Daar stond:
 *
 *     const dataInt16 = new Int16Array(data.buffer);
 *
 * Dat gaat op twee manieren mis. (1) Bij een ONEVEN aantal bytes gooit
 * Int16Array een RangeError en klapt het afspelen. (2) `data.buffer` is de
 * HELE onderliggende buffer, niet het stuk dat deze Uint8Array beslaat - staat
 * er een byteOffset op, dan lees je de verkeerde bytes. Beide gevallen zijn
 * zeldzaam en daarom vervelend: ze slaan pas toe als je het niet verwacht.
 */

export function base64NaarBytes(base64: string): Uint8Array {
  const binair = atob(base64);
  const bytes = new Uint8Array(binair.length);
  for (let i = 0; i < binair.length; i++) bytes[i] = binair.charCodeAt(i);
  return bytes;
}

/**
 * 16-bits PCM naar een AudioBuffer.
 *
 * Een halve sample aan het eind wordt weggelaten in plaats van een fout te
 * geven: één ontbrekende sample is 1/24000 seconde en dus onhoorbaar, terwijl
 * een fout het hele fragment kost.
 */
export function pcmNaarAudioBuffer(
  bytes: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
): AudioBuffer {
  const bruikbaar = bytes.byteLength - (bytes.byteLength % 2);
  if (bruikbaar === 0) throw new Error('Geen audio ontvangen.');

  // Kopiëren met een expliciete begin- en eindgrens. Zo maakt een byteOffset
  // op de bron niets uit, en is de uitlijning altijd goed.
  const kopie = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bruikbaar);
  const samples = new Int16Array(kopie);

  const buffer = ctx.createBuffer(1, samples.length, sampleRate);
  const kanaal = buffer.getChannelData(0);
  for (let i = 0; i < samples.length; i++) {
    // 32768 en niet 32767: dat is de schaal van een 16-bits sample.
    kanaal[i] = (samples[i] ?? 0) / 32768;
  }
  return buffer;
}

/**
 * Rauwe PCM in een WAV-jasje van 44 bytes.
 *
 * Nodig voor de TERUGVAL: als de AudioContext geweigerd wordt, kan hetzelfde
 * geluid nog via een <audio>-element. Dat element heeft veel soepelere regels
 * voor automatisch afspelen dan de Web Audio API - gemeten op 4 september 2026,
 * toen de opname van de spreker wel klonk en de vertaling niet, op hetzelfde
 * apparaat en in hetzelfde tabblad.
 */
export function pcmNaarWav(pcm: Uint8Array, sampleRate: number): ArrayBuffer {
  const bruikbaar = pcm.byteLength - (pcm.byteLength % 2);
  const buffer = new ArrayBuffer(44 + bruikbaar);
  const kop = new DataView(buffer);

  const schrijf = (plek: number, tekst: string): void => {
    for (let i = 0; i < tekst.length; i++) kop.setUint8(plek + i, tekst.charCodeAt(i));
  };

  schrijf(0, 'RIFF');
  kop.setUint32(4, 36 + bruikbaar, true);
  schrijf(8, 'WAVE');
  schrijf(12, 'fmt ');
  kop.setUint32(16, 16, true); // lengte van het fmt-blok
  kop.setUint16(20, 1, true); // 1 = ongecomprimeerde PCM
  kop.setUint16(22, 1, true); // mono
  kop.setUint32(24, sampleRate, true);
  kop.setUint32(28, sampleRate * 2, true); // bytes per seconde
  kop.setUint16(32, 2, true); // bytes per sample
  kop.setUint16(34, 16, true); // bits per sample
  schrijf(36, 'data');
  kop.setUint32(40, bruikbaar, true);

  new Uint8Array(buffer, 44).set(pcm.subarray(0, bruikbaar));
  return buffer;
}

export function duurSeconden(byteLengte: number, sampleRate: number): number {
  return Math.floor(byteLengte / 2) / sampleRate;
}

/**
 * De opname van de spreker zelf afspelen.
 *
 * ANDERE WEG DAN DE VERTAALDE SPRAAK. Die komt als rauwe PCM binnen en gaat
 * door de AudioContext. Deze komt als webm, ogg of m4a - een compleet
 * audiobestand met een eigen verpakking. Dat laat je de browser zelf
 * uitpakken met een <audio>-element; zelf decoderen zou drie codecs betekenen.
 */
export function speelBestand(
  base64: string,
  mimeType: string,
  signaal?: AbortSignal,
): Promise<void> {
  const bytes = base64NaarBytes(base64);
  // In een verse ArrayBuffer kopieren. Een Uint8Array kan in theorie op een
  // SharedArrayBuffer staan, en die accepteert Blob niet.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return speelBuffer(buffer, mimeType, signaal);
}

/**
 * Hoe lang we wachten tot het afspelen ECHT begint.
 *
 * Vijf seconden is royaal: normaal begint geluid binnen een tiende seconde.
 * Het gaat hier niet om de lengte van het fragment - er wordt alleen gewacht
 * tot `playing` vuurt, daarna mag het zo lang duren als het duurt.
 */
const START_GRENS_MS = 5000;

/**
 * Speelt een compleet audiobestand af via een <audio>-element.
 *
 * DE WEKKER IS GEEN OVERDRIJVING MAAR EEN GEMETEN REPARATIE (11-09-2026).
 *
 * Deze functie wachtte op `ended`, op `error`, of op een geweigerde `play()`.
 * Op een iPhone kan geen van die drie komen: onderbreekt iOS de geluidssessie -
 * door een telefoontje, Siri, geluid uit een andere app of het scherm dat op
 * slot gaat - dan blijft de belofte van `play()` gewoon HANGEN. Hij lost niet
 * op en hij faalt niet.
 *
 * Wat de gebruiker daarvan merkte: geen geluid, geen melding, en een
 * spreekknop die uit bleef staan omdat de speler dacht dat hij nog bezig was.
 * Bij Giovanni kwam alles na ongeveer 45 seconden alsnog los, in een keer
 * achter elkaar, omdat de hele rij achter dat ene fragment stond te wachten.
 * De server had zijn werk toen al lang gedaan: gemeten 0,7 tot 2,1 seconde voor
 * de vertaling en 2,1 tot 5,8 seconde voor de spraak.
 *
 * Nu wordt stil hangen een gewone fout. Dan verschijnt de oranje balk, en die
 * vertelt wel wat je moet doen.
 */
export function speelBuffer(
  buffer: ArrayBuffer,
  mimeType: string,
  /**
   * Om het afspelen van BUITENAF te kunnen stoppen.
   *
   * Zonder dit is een element dat eenmaal speelt niet meer te bereiken: het
   * leeft alleen in deze functie. De speler kon daardoor wel zijn Web
   * Audio-bron stoppen maar niet de terugvalweg - en juist die weg is wat
   * iemand hoort bij wie de AudioContext niet wil. Een stopknop die de helft
   * van de gevallen niet stopt, is geen stopknop.
   */
  signaal?: AbortSignal,
): Promise<void> {
  return new Promise((klaar, mis) => {
    // Al gestopt voordat er iets begon: dan niet alsnog beginnen.
    if (signaal?.aborted === true) {
      klaar();
      return;
    }

    const url = URL.createObjectURL(new Blob([buffer], { type: mimeType }));
    const speler = new Audio(url);
    let wekker: ReturnType<typeof setTimeout> | null = null;

    const opruimen = (): void => {
      if (wekker !== null) clearTimeout(wekker);
      wekker = null;
      signaal?.removeEventListener('abort', opAfbreken);
      URL.revokeObjectURL(url);
    };

    /**
     * EXPRES STOPPEN IS GEEN FOUT, en dat verschil is het hele punt.
     *
     * Zou dit afwijzen, dan vangt de aanroeper het als een mislukking en zet
     * de oranje balk "geluid staat uit" neer - bij iemand die alleen maar op
     * de stopknop drukte. Dus lost de belofte gewoon op, alsof het fragment
     * netjes is afgelopen.
     */
    const opAfbreken = (): void => {
      speler.pause();
      opruimen();
      klaar();
    };
    signaal?.addEventListener('abort', opAfbreken, { once: true });

    // `playing` en niet `play`: die eerste vuurt pas als er echt geluid uit
    // komt. `play` zegt alleen dat het gevraagd is, en dat is precies wat hier
    // stil bleef hangen.
    speler.onplaying = () => {
      if (wekker !== null) clearTimeout(wekker);
      wekker = null;
    };
    speler.onended = () => {
      opruimen();
      klaar();
    };
    speler.onerror = () => {
      opruimen();
      mis(new Error('opname niet af te spelen'));
    };

    wekker = setTimeout(() => {
      // Het element stoppen hoort erbij. Zonder dit kan hetzelfde fragment
      // later alsnog losbarsten, over een volgend bericht heen.
      speler.pause();
      opruimen();
      mis(new Error('afspelen begon niet'));
    }, START_GRENS_MS);

    void speler.play().catch((fout: unknown) => {
      opruimen();
      mis(fout instanceof Error ? fout : new Error('afspelen geweigerd'));
    });
  });
}
